import React from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  Col,
  Row,
  MultiSelection,
  Selection,
} from '@folio/stripes-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { Field } from 'react-final-form';
import { FaLink } from 'react-icons/fa';
import SettingsCardWrapper from './SettingsCardWrapper';
import { AUTHORITY_FIELD_OPTIONS } from '../../../../../shared/Authority/constants/defaultSettings';
import css from '../../styles.css';

function AuthoritySettingsCard({ authoritySourceOptions, pending }) {
  const { formatMessage } = useIntl();

  // Formatter for authority fields
  const authorityFieldFormatter = ({ option }) => (
    <div key={option.value}>
      {formatMessage({
        id: `ui-plugin-inventory-templates.authority.fields.${option.value}`,
        defaultMessage: option.label,
      })}
    </div>
  );

  // Formatter for authority sources
  const authoritySourceFormatter = ({ option }) => (
    <div key={option.value}>
      {formatMessage({
        id: `ui-plugin-inventory-templates.authority.sources.${option.value}`,
        defaultMessage: option.label,
      })}
    </div>
  );

  return (
    <SettingsCardWrapper
      titleId="ui-plugin-inventory-templates.templatesOtherSettings.sections.authoritySettings"
      defaultTitle="Authority Linking Settings"
      icon={FaLink}
    >
      <Row>
        <Col xs={12}>
          <Field
            id="enabledAuthorityFields"
            name="authority.enabledAuthorityFields"
            component={MultiSelection}
            label={
              <FormattedMessage
                id="ui-plugin-inventory-templates.templatesOtherSettings.fields.enabledAuthorityFields"
                defaultMessage="Fields enabled for authority linking"
              />
            }
            placeholder={formatMessage({
              id: 'ui-plugin-inventory-templates.templatesOtherSettings.fields.enabledAuthorityFields.placeholder',
              defaultMessage: 'Select fields',
            })}
            dataOptions={AUTHORITY_FIELD_OPTIONS}
            formatter={authorityFieldFormatter}
            loading={pending}
            disabled={pending}
            fullWidth
          />
        </Col>
        <Col className={css.infoMsg} style={{ fontSize: '14px' }}>
          <FormattedMessage
            id="ui-plugin-inventory-templates.templatesOtherSettings.fields.enabledAuthorityFields.info"
            defaultMessage="Select which fields should support authority linking. Only selected fields will show authority suggestions and linking options."
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Field
            id="enableAutoCreateAuthority"
            name="authority.enableAutoCreateAuthority"
            component={Checkbox}
            fullWidth
            label={
              <FormattedMessage
                id="ui-plugin-inventory-templates.templatesOtherSettings.fields.enableAutoCreateAuthority"
                defaultMessage="Enable automatic authority record creation"
              />
            }
            type="checkbox"
            onMouseDown={(e) => e.preventDefault()}
          />
        </Col>
        <Col className={css.infoMsg} style={{ fontSize: '14px' }}>
          <FormattedMessage
            id="ui-plugin-inventory-templates.templatesOtherSettings.fields.enableAutoCreateAuthority.info"
            defaultMessage="When enabled, users can create authority records directly from the cataloging interface when no matching authority is found."
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Field
            id="enableAutoLinking"
            name="authority.enableAutoLinking"
            component={Checkbox}
            fullWidth
            label={
              <FormattedMessage
                id="ui-plugin-inventory-templates.templatesOtherSettings.fields.enableAutoLinking"
                defaultMessage="Enable automatic authority linking"
              />
            }
            type="checkbox"
            onMouseDown={(e) => e.preventDefault()}
          />
        </Col>
        <Col className={css.infoMsg} style={{ fontSize: '14px' }}>
          <FormattedMessage
            id="ui-plugin-inventory-templates.templatesOtherSettings.fields.enableAutoLinking.info"
            defaultMessage="When enabled, the system will automatically link unlinked fields to existing authority records when opening records for editing."
          />
        </Col>
      </Row>

      {/* Authority Sources Header */}
      <Row>
        <Col xs={12}>
          <div
            style={{
              borderTop: '1px solid #ddd',
              paddingTop: '16px',
              marginTop: '8px',
              marginBottom: '16px',
            }}
          >
            <h4
              style={{
                margin: '0 0 8px 0',
                fontSize: '14px',
                fontWeight: '600',
                color: '#333',
              }}
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.templatesOtherSettings.sections.authoritySourcesHeader"
                defaultMessage="Authority Sources Configuration"
              />
            </h4>
            <div
              className={css.infoMsg}
              style={{ fontSize: '13px', marginBottom: '16px' }}
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.templatesOtherSettings.sections.authoritySourcesHeader.info"
                defaultMessage="Configure which authority source will be used when creating new authority records for each type. Only active sources are shown."
              />
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col xs={6}>
          <Field
            id="personalNameAuthoritySource"
            name="authority.sources.personalNameAuthoritySource"
            component={Selection}
            label={
              <FormattedMessage
                id="ui-plugin-inventory-templates.templatesOtherSettings.fields.personalNameAuthoritySource"
                defaultMessage="Personal name authority source"
              />
            }
            placeholder={formatMessage({
              id: 'ui-plugin-inventory-templates.templatesOtherSettings.fields.personalNameAuthoritySource.placeholder',
              defaultMessage: 'Select source',
            })}
            dataOptions={authoritySourceOptions}
            formatter={authoritySourceFormatter}
            loading={pending}
            disabled={pending}
            fullWidth
          />
        </Col>
        <Col xs={6}>
          <Field
            id="corporateNameAuthoritySource"
            name="authority.sources.corporateNameAuthoritySource"
            component={Selection}
            label={
              <FormattedMessage
                id="ui-plugin-inventory-templates.templatesOtherSettings.fields.corporateNameAuthoritySource"
                defaultMessage="Corporate name authority source"
              />
            }
            placeholder={formatMessage({
              id: 'ui-plugin-inventory-templates.templatesOtherSettings.fields.corporateNameAuthoritySource.placeholder',
              defaultMessage: 'Select source',
            })}
            dataOptions={authoritySourceOptions}
            formatter={authoritySourceFormatter}
            loading={pending}
            disabled={pending}
            fullWidth
          />
        </Col>
      </Row>

      <Row>
        <Col xs={6}>
          <Field
            id="conferenceNameAuthoritySource"
            name="authority.sources.conferenceNameAuthoritySource"
            component={Selection}
            label={
              <FormattedMessage
                id="ui-plugin-inventory-templates.templatesOtherSettings.fields.conferenceNameAuthoritySource"
                defaultMessage="Conference name authority source"
              />
            }
            placeholder={formatMessage({
              id: 'ui-plugin-inventory-templates.templatesOtherSettings.fields.conferenceNameAuthoritySource.placeholder',
              defaultMessage: 'Select source',
            })}
            dataOptions={authoritySourceOptions}
            formatter={authoritySourceFormatter}
            loading={pending}
            disabled={pending}
            fullWidth
          />
        </Col>
        <Col xs={6}>
          <Field
            id="uniformTitleAuthoritySource"
            name="authority.sources.uniformTitleAuthoritySource"
            component={Selection}
            label={
              <FormattedMessage
                id="ui-plugin-inventory-templates.templatesOtherSettings.fields.uniformTitleAuthoritySource"
                defaultMessage="Uniform title authority source"
              />
            }
            placeholder={formatMessage({
              id: 'ui-plugin-inventory-templates.templatesOtherSettings.fields.uniformTitleAuthoritySource.placeholder',
              defaultMessage: 'Select source',
            })}
            dataOptions={authoritySourceOptions}
            formatter={authoritySourceFormatter}
            loading={pending}
            disabled={pending}
            fullWidth
          />
        </Col>
      </Row>

      <Row>
        <Col xs={6}>
          <Field
            id="topicalSubjectAuthoritySource"
            name="authority.sources.topicalSubjectAuthoritySource"
            component={Selection}
            label={
              <FormattedMessage
                id="ui-plugin-inventory-templates.templatesOtherSettings.fields.topicalSubjectAuthoritySource"
                defaultMessage="Topical subject authority source"
              />
            }
            placeholder={formatMessage({
              id: 'ui-plugin-inventory-templates.templatesOtherSettings.fields.topicalSubjectAuthoritySource.placeholder',
              defaultMessage: 'Select source',
            })}
            dataOptions={authoritySourceOptions}
            formatter={authoritySourceFormatter}
            loading={pending}
            disabled={pending}
            fullWidth
          />
        </Col>
      </Row>
    </SettingsCardWrapper>
  );
}

AuthoritySettingsCard.propTypes = {
  authoritySourceOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  pending: PropTypes.bool.isRequired,
};

export default AuthoritySettingsCard;
